import {Component, EventEmitter, Inject, Input, OnInit, OnChanges, SimpleChanges, Output} from '@angular/core';
import {QuickApplyApiService} from "../quick-apply-api.service";
import {TalentHappyPathDataService} from "../../shared/service/talent-happy-path-data-service";
import { EnvironmentToken } from 'src/util';
import { Environment } from 'src/typings';
import { Countries, FileUploadUserType } from 'src/app/enums';

@Component({
  selector: 'app-file-upload',
  standalone: false,
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss'
})
export class FileUploadComponent implements OnInit, OnChanges {

  @Output() uploadSuccessEvent = new EventEmitter<boolean>();
  @Output() resumeUploadingEvent = new EventEmitter<boolean>();
  @Output() fileSelectedEvent = new EventEmitter<File>();
  @Input() talentId: number;
  @Input() fileUploadUserType: FileUploadUserType;
  @Input() isJapanOrder: boolean = false;
  @Input() isOptional: boolean = false;
  @Input() isNewTHPTalent: boolean = false;
  FileUploadUserType: typeof FileUploadUserType = FileUploadUserType;
  Countries = Countries;

  files: any[] = [];

  fileName: string = '';
  validExtensions :string[] = ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpg', 'image/jpeg', 'image/png', 'text/plain', 'application/pdf', 'doc', 'docx', 'text/rtf', 'application/rtf', 'text/html' ];
  validFileTypeSelected: boolean = true;
  fileSelected: boolean = false;
  uploading: boolean = false;
  uploadSuccess: boolean = false;
  showLatestResume: boolean = false;
  uploadError: boolean = false;
  resumeDetail: ResumeDetail;
  matUrl: string;
  resumeLastUpdatedDate: string;
  countryId: number;

  constructor(
    private quickApplyService: QuickApplyApiService,
    private talentHappyPathDataService: TalentHappyPathDataService,
    @Inject(EnvironmentToken) private environment: Environment) {
  }

  ngOnInit(): void {
    this.countryId = this.talentHappyPathDataService.postingInfo?.countryId;
    this.resumeDetail = this.talentHappyPathDataService.talent?.resumeData;
    if (this.resumeDetail && !this.isNewTHPTalent) {
      this.uploadSuccess = true;
    }
    this.loadTalentLatestResume();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['talentId'] && !changes['talentId'].firstChange) {
      this.loadTalentLatestResume();
    }
  }

  private loadTalentLatestResume(): void {
    if (this.fileUploadUserType === FileUploadUserType.GATHER && this.talentId && !this.isNewTHPTalent) {
      this.quickApplyService.readTalentLatestResume(this.talentId).subscribe(latestResume => {
        if (latestResume) {
          this.showLatestResume = true;
          this.resumeDetail = latestResume;
        }
      },
      error => {
        console.log("There was an error retrieving the talent's latest resume:");
        console.log(error);
      });
    }
  }

  handleFileUpload(file: File): void {
    if (!file) return;

    if (!this.isJapanOrder) {
      this.uploadFile(file);
    } else {
      if (this.validExtensions.includes(file.type)) {
        this.uploadSuccess = true;
        this.uploading = false;
        this.fileName = file.name;
        this.fileSelectedEvent.emit(file);
        if (!this.isOptional || this.resumeDetail) {
          this.uploadSuccessEvent.emit(true);
          this.resumeUploadingEvent.emit(false);
        }
        this.resumeDetail = this.mockResumeForDisplay(file);
      }
      else {
        this.validFileTypeSelected = false;
      }
    }
  }

  onFileDrop(files: Array<any>): void {
    const file = files[0];
    this.handleFileUpload(file);
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    this.handleFileUpload(file);
  }

  /** Since Japan resume parsing is done after applying, this function mocks the ResumeDetail object from selected file to display in the
      file-upload container */
  mockResumeForDisplay(file: File) : ResumeDetail {
    return {
      personId: undefined,
      experience: [],
      education: [],
      skills: [],
      isParsed: false,
      fileName: file.name,
      lastUpdatedDate: new Date(),
      lastUpdatedDateLocaleString: new Date().toLocaleString(),
      fileId: -1,
      mimeType: file.type,
      contents: file,
      isToBeDisplayed: true, // Default to true
      plainTextContents: "", // Can be populated later if needed
      status: 1, //New
      resumeSummary: "",
      rawResponse: "",
      optionalFileName: file.name
    }
  }

  uploadFile(file: File) {
    this.uploadSuccess = false;
    this.uploadError = false;
    this.fileName = file.name;
    if (this.validExtensions.includes(file.type)) {
      let talentId = this.talentHappyPathDataService.talent.personId ? this.talentHappyPathDataService.talent.personId : -1;
      this.uploading = true;
      this.resumeUploadingEvent.emit(true);
      this.validFileTypeSelected = true;
      const upload$ = this.quickApplyService.saveResume(file, talentId);
      upload$.subscribe(
        data => {
          this.resumeDetail = data;
          this.uploadSuccess = true;
          this.uploading = false;
          this.uploadSuccessEvent.emit(true);
          this.resumeUploadingEvent.emit(false);
          this.talentHappyPathDataService.resumeDetail = data;
        },
        error => {
          this.uploading = false;
          this.uploadError = true;
          this.uploadSuccessEvent.emit(false);
          this.resumeUploadingEvent.emit(false);
          this.talentHappyPathDataService.resumeDetail = undefined;
          this.validFileTypeSelected = false;
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          console.error(`Error uploading resume: ${error.message}`);
        });
    } else {
      this.validFileTypeSelected = false;
    }
  }
}
